import {FilterFactory, TermFilter} from '@dofleini/query-builder';
import {ValidatorProvider} from '@dofleini/validator';
import {Checkbox, Col, Form, Input, InputNumber, Row, Select, Switch} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import MunicipalitySelect from '@/components/MunicipalitySelect';
import ProvinceSelect from '@/components/ProvinceSelect';
import UploadFileDragger from '@/components/UploadFileDragger';

const QuestionWithDetails = ({
  id,
  value,
  label,
  translate,
  onChange,
  extraLabel,
  requireLocation,
  customWhereCmp,
  placeholderText,
  withFile,
  fileLabel,
  fileUploadLabel,
  optionsDirection = 'column',
  cmpProps,
  form,
  filters = {},
  options = [],
  help,
  ...rest
}) => {
  console.info(id, value);
  const {t} = useTranslation(translate || 'common');
  let [data, setData] = useState(value || {});
  const province = form?.getFieldValue?.([id, 'address', 'province']) || data?.address?.province;
  const municipality = form?.getFieldValue?.([id, 'address', 'municipality']) || data?.address?.municipality;
  
  useEffect(() => {
    if (value) {
      setData(value || {});
    }
  }, [value]);
  
  const onChangeValue = useCallback((value, key) => {
    set(data, key, value);
    setData({...data});
    onChange && onChange({...data});
  }, [data, onChange]);
  
  const onDetailsCmpValueChange = useCallback((e, key) => {
    if (customWhereCmp) onChangeValue(e, 'reference'); else onChangeValue(e?.target?.value, key || 'description');
  }, [customWhereCmp, onChangeValue]);
  
  const onActiveChange = useCallback((e) => {
    onChangeValue(e?.target?.checked, 'active');
    if (!e?.target?.checked) {
      onChangeValue(null, 'reference');
      onChangeValue(null, 'description');
      if (requireLocation) onChangeValue(null, 'address');
      if (!isEmpty(options))
        options?.forEach((e) => onChangeValue(null, e));
    }
  }, [onChangeValue, options, requireLocation]);
  
  const onChangeProvince = useCallback((e) => {
    onChangeValue(e, ['address', 'province']);
  }, [onChangeValue]);
  
  const onChangeMunicipality = useCallback((e) => {
    onChangeValue(e, ['address', 'municipality']);
  }, [onChangeValue]);
  
  const DetailsCmp = customWhereCmp || Input;
  
  const _filters = useMemo(() => {
    if (!requireLocation || !province) return filters;
    
    const provF = new TermFilter({field: 'address.province', value: province});
    const munF = municipality ? new TermFilter({field: 'address.municipality', value: municipality}) : null;
    
    if (munF) return FilterFactory.add(provF, munF).toQuery();
    
    return provF.toQuery();
  }, [filters, municipality, province, requireLocation]);
  
  return (<div id={id}>
    <div className="mb-2">
      <Checkbox checked={data?.active} className={classNames('mx-0 my-px')} size={'large'}
        onChange={onActiveChange}>{t(label)}</Checkbox>
      <div>{help}</div>
    </div>
    {value?.active && <div>
      {data?.active && requireLocation && <Row gutter={12}>
        <Col span={12} xs={24} lg={12}>
          <Form.Item name={[id, 'address', 'province']} required={data?.active} rules={[{required: true}]}
            label={t('Provincia')}>
            <ProvinceSelect
              canClear
              disabled={!value?.active}
              size={'large'}
              placeholder={t('selectPlaceholder')}
              onChange={onChangeProvince}
            />
          </Form.Item>
        </Col>
        <Col span={12} xs={24} lg={12}>
          <Form.Item
            dependencies={[[id, 'address', 'province']]}
            name={[id, 'address', 'municipality']}
            label={t('municipality')}
            required={data?.active}
            rules={[{required: true}]}
          >
            <MunicipalitySelect
              canClear
              province={province}
              size={'large'}
              placeholder={t('selectPlaceholder')}
              onChange={onChangeMunicipality}
            />
          </Form.Item>
        </Col>
      </Row>}
      <div
        className={classNames('flex w-full',
          {'flex-row items-center flex-wrap gap-x-8': optionsDirection === 'row'},
          {'flex-col gap-2': optionsDirection === 'column'}
        )}
      >
        {!isEmpty(options) ? options?.map(
          ({required, placeholder, type, field, validation, options: fieldOptions, label: optionLabel, help}) => {
            const placeholderKey = placeholder || placeholderText || 'defaultPlaceholder';
            const component = () => {
              switch (type) {
                case 'file':
                  return (<UploadFileDragger
                    {...rest}
                    isImage={false}
                    translation={translate}
                    listType={'picture'}
                    maxCount={1}
                    descriptionText={fileUploadLabel}
                    onChange={(v) => onDetailsCmpValueChange({target: {value: v}}, field)}
                  />);
                case 'boolean':
                  return <Switch
                    {...rest}
                    checked={data[field] === 'Si'}
                    onChange={(v) => onDetailsCmpValueChange({target: {value: v ? 'Si' : 'No'}}, field)}
                    checkedChildren={t('yes')}
                    unCheckedChildren={t('no')}
                  />;
                case 'textarea':
                  return <TextArea
                    {...rest}
                    autoSize={{minRows: 2, maxRows: 6}}
                    size={'large'}
                    placeholder={t(placeholderKey)}
                    disabled={!value?.active}
                    onChange={(v) => onDetailsCmpValueChange(v, field)}
                  />;
                case 'select':
                  return <Select
                    allowClear
                    {...rest}
                    options={fieldOptions?.map(o => ({...o, label: t(extraLabel + '.' + o?.label)}))}
                    size={'large'}
                    placeholder={t(placeholderKey)}
                    disabled={!value?.active}
                    onChange={(v) => onDetailsCmpValueChange({target: {value: v}}, field)}
                  />;
                case 'number':
                  return (<InputNumber
                    {...rest}
                    {...{
                      min: 0,
                      size: 'large',
                      placeholder: 0
                    }}
                    size={'large'}
                    placeholder={t(placeholderKey)}
                    disabled={!value?.active}
                    onChange={(v) => onDetailsCmpValueChange({target: {value: v}}, field)}
                  />);
                default:
                  return (<Input
                    {...rest}
                    required
                    size={'large'}
                    placeholder={t(placeholderKey)}
                    disabled={!value?.active}
                    onChange={(v) => onDetailsCmpValueChange(v, field)}
                  />);
              }
            };
            return <Form.Item
              {...cmpProps}
              key={field + type}
              name={[id, field]}
              label={t(optionLabel || (extraLabel + '.' + field))}
              required={required}
              rules={!validation ? [{required: required}] : [{required: required}, validation]?.filter(v => !!v)}
              labelCol={{flex: 'auto'}}
              wrapperCol={{flex: '1'}}
              className={classNames('mb-1', {
                'flex items-center flex-row-reverse gap-x-2 form-item-row-position': type === 'boolean',
                'w-full': type !== 'boolean'
              })}
              initialValue={data?.[field]}
              help={help}
            >
              {component()}
            </Form.Item>;
          }) : <Form.Item
          {...cmpProps}
          name={[id, (customWhereCmp ? 'reference' : 'description')]}
          label={t(extraLabel)}
          required
          rules={[{required: true}]}
          className={classNames('mb-0')}
          initialValue={customWhereCmp ? data?.reference : data?.description}
        >
          <DetailsCmp
            {...rest}
            required
            size={'large'}
            placeholder={t(placeholderText || 'defaultPlaceholder')}
            disabled={!value?.active}
            filters={_filters}
            onChange={onDetailsCmpValueChange}
          />
        </Form.Item>}
        {withFile && <Form.Item label={t(fileLabel)} name={[id, 'file']} className="mb-0"
          validateStatus={!data?.file ? 'error' : null} initialValue={data?.file}>
          <UploadFileDragger
            isImage={false}
            translation={translate}
            listType={'picture'}
            maxCount={1}
            descriptionText={fileUploadLabel}
          />
        </Form.Item>}
      </div>
    </div>}
  </div>);
};

QuestionWithDetails.propTypes = {
  value: PropTypes.object,
  label: PropTypes.string,
  id: PropTypes.string,
  fileLabel: PropTypes.string,
  fileUploadLabel: PropTypes.string,
  withFile: PropTypes.bool,
  customWhereCmp: PropTypes.any,
  filters: PropTypes.any,
  form: PropTypes.any,
  help: PropTypes.any,
  requireLocation: PropTypes.bool,
  extraLabel: PropTypes.string,
  optionsDirection: PropTypes.string,
  placeholderText: PropTypes.string,
  translate: PropTypes.string,
  cmpProps: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func
};

QuestionWithDetails.defaultProps = {};

export default memo(QuestionWithDetails);

export const renderQuestionWithDetails = ({formItemProps, field, form}) => {
  
  const rules = field?.required && [{required: true, message: ValidatorProvider.t('validateMessages.required')}];
  
  return (<Form.Item {...formItemProps} required={field?.required} rules={rules} label={null}>
    <QuestionWithDetails {...field?.widgetProps} {...field} form={form}/>
  </Form.Item>);
};
